import React, { useLayoutEffect, useRef } from 'react';
import AnimatedWords from './AnimatedWords';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

const ServicesFeatures = () => {
    const containerRef = useRef(null);

    useLayoutEffect(() => {
        gsap.registerPlugin(ScrollTrigger);
        const ctx = gsap.context(() => {
            const cards = containerRef.current?.querySelectorAll('[data-feature-card]');
            if (!cards) return;
            cards.forEach((card) => {
                const headingWords = card.querySelectorAll('h3 [data-animated-word]');
                const para = card.querySelector('p');
                const tl = gsap.timeline({
                    defaults: { ease: 'power2.out' },
                    scrollTrigger: {
                        trigger: card,
                        start: 'top 80%',
                        toggleActions: 'play none none none',
                        once: true
                    }
                });
                if (headingWords && headingWords.length) {
                    tl.fromTo(headingWords, { yPercent: 100 }, { yPercent: 0, duration: 0.5, ease: 'power3.out', stagger: 0.06, clearProps: 'transform' }, 0);
                }
                if (para) {
                    tl.fromTo(para, { filter: 'blur(18px)', opacity: 0, y: 8 }, { filter: 'blur(0px)', opacity: 1, y: 0, duration: 0.6 }, '>-0.05');
                }
            });
        }, containerRef);
        return () => ctx.revert();
    }, []);

    const features = [
        {
            id: 1,
            title: "Reliable & On-Time Delivery",
            description: "Ensuring fast, efficient, and on-time deliveries through optimized routes, real-time tracking, and expert logistics management.",
            image: "/images/reliable_delivery.webp",
            icon: "truck"
        },
        {
            id: 2,
            title: "Customer-Centric Service",
            description: "Dedicated support, flexible solutions, and transparent communication built around your business needs.",
            image: "/images/customer_centric.webp",
            icon: "box"
        },
        {
            id: 3,
            title: "Global Reach & Network",
            description: "Connecting cities and industries with seamless Air, Rail, and Surface (LTL & FTL) transport solutions across India.",
            image: "/images/global_research.webp",
            icon: "globe"
        }
    ];

    const getIconSvg = (iconType) => {
        switch (iconType) {
            case 'truck':
                return (
                    <img src="/images/delivery_truck_speed.webp" height="36" width="36" loading="lazy" decoding="async" alt="truck_icon" />
                );
            case 'box':
                return (
                    <img src="/images/package_2.webp" height="36" width="36" loading="lazy" decoding="async" alt="package_icon" />
                );
            case 'globe':
                return (
                    <img src="/images/globe_location_pin.webp" height="36" width="36" loading="lazy" decoding="async" alt="world_icon"/>
                );
            default:
                return null;
        }
    };

    return (
        <section id="services" className="py-16 bg-white">
            <div className="container mx-auto px-4" ref={containerRef}>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                    {features.map((feature) => (
                        // 1. Main wrapper: Relative, with margin-bottom for spacing
                        <div key={feature.id} className="relative mb-16 max-w-[482px]" data-feature-card>

                            {/* 1st Component: Image */}
                            <div className="relative h-48 w-[95%] ">
                                <img
                                    src={feature.image}
                                    alt={feature.title}
                                    className="w-full h-full object-cover rounded-lg" // Rounded top corners
                                    loading="lazy"
                                    decoding="async"
                                />
                            </div>

                            {/* 2nd Component: Content (Text Card) */}
                            {/* This overlaps the image with negative margin (-mt-10) */}
                            <div className="flex flex-col justify-center items-center relative !bg-[#FFF6F6] z-10 w-[90%] p-6 ml-4 max-w-[411px] h-[220px] -mt-10 bg-gray-50 rounded-lg shadow-lg hover:shadow-xl transition-shadow duration-300">
                                <h3 className="text-base md:text-lg lg:text-xl self-start font-bold text-gray-900 mb-3">
                                    <AnimatedWords auto={false} lines={[feature.title]} />
                                </h3>
                                <p className="text-gray-700 leading-relaxed text-sm md:text-base">
                                    {feature.description}
                                </p>


                            </div>
                            {/* 3rd Component: Icon (MOVED INSIDE THE TEXT CARD) */}
                            <div className="absolute z-20 bottom-0 right-0 w-16 h-16 rounded-l-lg bg-red-600 flex items-center justify-center text-white shadow-lg">
                                {getIconSvg(feature.icon)}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
};

export default ServicesFeatures;
